import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, startWith, take, takeUntil } from 'rxjs/operators';
import { setTitle } from 'src/app/actions/core.actions';
import { findProblemImages } from 'src/app/actions/try-search.action';
import { ImageType } from 'src/app/models/try-search';
import { RootState } from 'src/app/reducers';
import { STATIC_DATA_CACHE_DAYS, SUBJECT_NAMES } from 'src/app/resources/config';
import { getKakomonImage } from 'src/app/selectors/try-search.selector';
import { ProblemImageUtil } from 'src/app/utils/problem-image-util';
import { environment } from 'src/environments/environment';
import { setCommonIdBrowserTitle } from '../../../../actions/common-id/common-id-core.actions';
import { getFetchedDate } from '../../../../selectors/static-data.selectors';
import { Log } from '../../../../utils/log';
import { findStaticData, initializeStaticDataState } from '../../../../actions/static-data.actions';
import { Dates } from '../../../../utils/dates';
import { getUniversities } from '../../../../selectors/static-data.selectors';
import { WINDOW_OBJECT } from '../../../../utils/injection-tokens';

@Component({
  selector: 'app-try-kakomon-image',
  templateUrl: './kakomon-image.component.html',
  styleUrls: ['./kakomon-image.component.scss']
})
export class TryKakomonImageComponent implements OnInit, OnDestroy {
  private LOG_SOURCE = this.constructor.name;
  private title = '';

  private destroy$ = new Subject<void>();

  loaded$: Observable<boolean>;
  paramType$: Observable<ImageType>;
  questionImages$: Observable<string[]>;
  answerImages$: Observable<string[]>;
  fImages$: Observable<string[]>;
  isError$: Observable<boolean>;

  constructor(private store: Store<RootState>, private route: ActivatedRoute, @Inject(WINDOW_OBJECT) private window: Window) {}

  ngOnInit() {
    this.setUp();

    const params$ = this.route.queryParams.pipe(filter(it => it != null));
    const universities$ = this.store.select(getUniversities).pipe(filter(it => it != null));
    combineLatest([universities$, params$])
      .pipe(
        filter(it => it != null),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(([universities, params]) => {
        const problemId = params.paperId + params.daimon_Id;
        this.store.dispatch(findProblemImages({ problemId }));

        const universityId = params.paperId.toString().substring(2, 6);
        const university = universities.find(item => item.id === `D${universityId}`);
        const subject = SUBJECT_NAMES[params.paperId.toString().substring(6, 8)];
        const imageType = params.type === 'a' ? '解答' : '問題';

        this.title = `${university.name} ${subject} ${imageType}`;
        this.store.dispatch(setCommonIdBrowserTitle({ subTitle: this.title }));
        setTimeout(() => this.store.dispatch(setTitle({ title: this.title })));
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  clickImage(src: string) {
    this.window.parent.postMessage(
      {
        type: 'click',
        target: this.getLambdaImagePath(src)
      },
      '*'
    );
  }

  /**
   * f 画像は画像本来のサイズで表示するため、@2x 画像（2 倍解像度）はそのままだと等倍画像の 2 倍の大きさになる。
   * 実サイズの半分を CSS 上の幅として指定し、等倍画像と同じ大きさで表示する。
   */
  onFImageLoaded(event: Event) {
    const image = event.target as HTMLImageElement;
    if (!image.src.includes('@2x')) return;
    image.style.width = `${image.naturalWidth / 2}px`;
  }

  private setUp() {
    this.findStaticDataIfNeeded();

    this.loaded$ = this.store.select(getKakomonImage).pipe(
      map(it => it != null),
      startWith(false)
    );

    this.paramType$ = this.route.queryParams.pipe(
      filter(it => it != null),
      take(1),
      map(params => (params.type === 'a' ? 'a' : params.type === 'f' ? 'f' : 'q') as ImageType)
    );

    this.questionImages$ = this.store.select(getKakomonImage).pipe(
      filter(it => it != null),
      take(1),
      map(kakomonImages => kakomonImages.questionImages.map(image => this.getImagePath(image)))
    );

    this.answerImages$ = this.store.select(getKakomonImage).pipe(
      filter(it => it != null),
      take(1),
      map(kakomonImages => kakomonImages.answerImages.map(image => this.getImagePath(image)))
    );

    this.fImages$ = this.store.select(getKakomonImage).pipe(
      filter(it => it != null),
      take(1),
      map(kakomonImages => (kakomonImages.fImage ? [this.getImagePath(kakomonImages.fImage)] : []))
    );

    this.isError$ = combineLatest([this.paramType$, this.questionImages$, this.answerImages$, this.fImages$]).pipe(
      filter(it => it != null),
      take(1),
      map(
        ([paramType, questionImages, answerImages, fImages]) =>
          (paramType === 'q' && questionImages.length === 0) ||
          (paramType === 'a' && answerImages.length === 0) ||
          (paramType === 'f' && fImages.length === 0)
      )
    );

    this.isError$.pipe(takeUntil(this.destroy$)).subscribe(isError => {
      if (isError) {
        this.window.parent.postMessage(
          {
            type: 'invalid_page'
          },
          '*'
        );
      }
    });

    const kakomonImages$ = this.store.select(getKakomonImage).pipe(
      filter(it => it != null),
      take(1)
    );
    combineLatest([this.paramType$, kakomonImages$])
      .pipe(
        filter(it => it != null),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(([paramType, kakomonImages]) => {
        // f 画像は親ウィンドウへの init 連携（保存・印刷用）の対象外
        if (paramType === 'f') return;

        const targetImages = paramType === 'q' ? kakomonImages.questionImages : kakomonImages.answerImages;
        const images = targetImages.map(image => this.getLambdaImagePath(image));
        if (images.length > 0) {
          this.window.parent.postMessage(
            {
              type: 'init',
              images
            },
            '*'
          );
        }
      });
  }

  private getImagePath(image: string): string {
    const path = ProblemImageUtil.getPath(image, false);
    return `${environment.cloudfrontHost}${path}`;
  }

  private getLambdaImagePath(image: string): string {
    const splitedPath = image.split('/');
    const fileName = splitedPath[splitedPath.length - 1];
    const path = ProblemImageUtil.getPath(fileName, false);
    return `${environment.lambdaHost}?key=${path}`;
  }

  private findStaticDataIfNeeded() {
    this.store
      .select(getFetchedDate)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(fetchedDate => {
        if (!fetchedDate) {
          Log.debug(this.LOG_SOURCE, `static data が存在していないため取得します`);
          this.store.dispatch(findStaticData());
          return;
        }
        const cacheExpired = Dates.isCachedDateExpired(fetchedDate, STATIC_DATA_CACHE_DAYS);
        if (cacheExpired) {
          Log.debug(this.LOG_SOURCE, `cache 期間を超過したため再度 static data を取得します. fetchedDate: ${fetchedDate}`);
          this.store.dispatch(initializeStaticDataState());
          this.store.dispatch(findStaticData());
          return;
        }
        Log.debug(this.LOG_SOURCE, 'static data が取得済みのため何もしません');
      });
  }
}
